import * as Tabs from '@radix-ui/react-tabs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Comments } from '../components/lead/Comments';
import { Documents } from '../components/lead/Documents';
import { History } from '../components/lead/History';
import { Observations } from '../components/lead/Observations';
import { MoveLeadSheet } from '../components/kanban/MoveLeadSheet';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { useFlowchart } from '../hooks/use-flowchart';
import { useLead, useMoveLead } from '../hooks/use-leads';
import { useAuth } from '../lib/auth-context';

interface LeadDetail {
  id: string;
  projectId: string;
  flowchartId: string;
  currentStageId: string;
  title: string;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  company: string | null;
  valueCents: number | null;
  currency: string | null;
  source: string | null;
  customFields: Record<string, unknown>;
  observations: string;
  assignedTo: { id: string; fullName: string; email: string } | null;
  enteredStageAt: string;
  currentStage: { id: string; slug: string; name: string; color: string };
  counts: { comments: number; documents: number; reminders: number; history: number };
}

export function LeadDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const lead = useLead(id);
  const data = lead.data as LeadDetail | undefined;
  const flowchart = useFlowchart(data?.flowchartId);
  const move = useMoveLead(id ?? '', data?.projectId ?? '');
  const [moveOpen, setMoveOpen] = useState(false);
  const { t } = useTranslation();
  const auth = useAuth();

  if (lead.isLoading) return <Skeleton className="h-64" />;
  if (!data) return <p className="text-sm text-surface-500">{t('lead.notFound')}</p>;

  const outgoing = (flowchart.data?.transitions ?? []).filter(
    (tr) => tr.fromStageId === data.currentStageId,
  );
  const stagesById = Object.fromEntries(
    (flowchart.data?.stages ?? []).map((s) => [s.id, s]),
  ) as Record<string, NonNullable<typeof flowchart.data>['stages'][number]>;

  return (
    <div className="space-y-6">
      <header className="space-y-2">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold">{data.title}</h1>
            <p className="text-sm text-surface-500">
              {data.company ?? '—'} · {data.contactName ?? t('lead.noContact')}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span
              className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium"
              style={{ backgroundColor: data.currentStage.color + '22', color: data.currentStage.color }}
            >
              {data.currentStage.name}
            </span>
            <Button onClick={() => setMoveOpen(true)}>{t('lead.move')}</Button>
          </div>
        </div>
        <dl className="grid gap-x-4 gap-y-1 text-sm sm:grid-cols-3">
          <div>
            <dt className="text-xs uppercase text-surface-400">{t('lead.email')}</dt>
            <dd>{data.contactEmail ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase text-surface-400">{t('lead.phone')}</dt>
            <dd>{data.contactPhone ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase text-surface-400">{t('lead.value')}</dt>
            <dd>
              {data.valueCents !== null && data.currency
                ? new Intl.NumberFormat(undefined, { style: 'currency', currency: data.currency }).format(data.valueCents / 100)
                : '—'}
            </dd>
          </div>
        </dl>
      </header>

      <Observations
        leadId={data.id}
        observations={data.observations ?? ''}
        canEdit={!!auth.user && auth.user.role !== 'VIEWER'}
      />

      <Tabs.Root defaultValue="comments" className="space-y-4">
        <Tabs.List className="flex border-b border-surface-200">
          {(
            [
              ['comments', t('lead.tabsComments'), data.counts.comments],
              ['documents', t('lead.tabsDocuments'), data.counts.documents],
              ['history', t('lead.tabsHistory'), data.counts.history],
            ] as const
          ).map(([value, label, count]) => (
            <Tabs.Trigger
              key={value}
              value={value}
              className="px-3 py-2 text-sm font-medium text-surface-600 data-[state=active]:border-b-2 data-[state=active]:border-brand-600 data-[state=active]:text-brand-700"
            >
              {label} <span className="text-xs text-surface-400">({count})</span>
            </Tabs.Trigger>
          ))}
        </Tabs.List>
        <Tabs.Content value="comments">
          <Comments leadId={data.id} />
        </Tabs.Content>
        <Tabs.Content value="documents">
          <Documents leadId={data.id} />
        </Tabs.Content>
        <Tabs.Content value="history">
          <History leadId={data.id} />
        </Tabs.Content>
      </Tabs.Root>

      <MoveLeadSheet
        open={moveOpen}
        onOpenChange={setMoveOpen}
        leadTitle={data.title}
        outgoingTransitions={outgoing}
        stagesById={stagesById}
        busy={move.isPending}
        onMove={async (input) => {
          await move.mutateAsync(input);
        }}
      />
    </div>
  );
}
